import React, { useState, useMemo } from 'react'
import { Card, CardContent, Grid, Chip } from '@material-ui/core'
import DeleteIcon from '@material-ui/icons/DeleteForever'
import cn from 'classnames'

import SubmitButton from 'Components/Form/SubmitButton'
import Form, { Input, Checkbox } from 'Components/Form'
import { updateChannels } from '../../../gql'

const Edit = ({ country, classes, edit, setEdit, refetch }) => {
  const [channels, setChannels] = useState({
    ...edit.channels,
    isAllActive: edit.channels.standard.every(item =>
      edit.channels.selected.includes(item),
    ),
  })
  const [newChannel, setNewChannel] = useState('')
  const minSelectedhannels = channels.selected.length < 3

  const createChannel = () => {
    setChannels(channels => {
      let { custom, selected, standard } = channels
      const trimChannel = newChannel.trim()
      const addChannel = channelArray => {
        if (channelArray.includes(trimChannel)) {
          if (!selected.includes(trimChannel)) {
            selected = [...selected, trimChannel]
            setNewChannel('')
          }
          return true
        }
        return false
      }

      if (addChannel(standard)) return { ...channels, selected }
      if (addChannel(custom)) return { ...channels, custom, selected }
      setNewChannel('')
      return {
        ...channels,
        custom: [...custom, trimChannel],
        selected: [...selected, trimChannel],
      }
    })
  }

  const selectChannel = (value, isChecked) => {
    setChannels(channels => {
      let { selected, standard, isAllActive } = channels
      if (isChecked) {
        if (value === 'all') {
          selected = [...new Set([...selected, ...standard])]
        } else {
          selected = [...selected, value]
        }
      } else {
        if (value === 'all') {
          selected = selected.filter(item => !standard.includes(item))
        } else {
          selected = selected.filter(item => item !== value)
        }
      }
      isAllActive = standard.every(item => selected.includes(item))
      return {
        ...channels,
        selected,
        isAllActive,
      }
    })
  }

  const deleteChannel = value => {
    setChannels(channels => {
      let { selected, custom } = channels
      selected = selected.filter(item => item !== value)
      custom = custom.filter(item => item !== value)

      return {
        ...channels,
        selected,
        custom,
      }
    })
  }

  const saveChannels = async () => {
    const { custom, selected } = channels
    await updateChannels({
      nodes: {
        countryId: country.id,
        selectedChannels: selected,
        customChannels: custom,
      },
    })
    refetch()
    setEdit(edit => ({
      ...edit,
      mode: !edit.mode,
      channels: {
        ...edit.channels,
        custom,
        selected,
      },
    }))
  }

  const error = useMemo(() => {
    const trimChannel = newChannel.trim()
    if (!trimChannel) {
      return true
    } else if (trimChannel.length > 50) {
      return 'Please reduce input length to 50 characters or less'
    } else if (trimChannel.length < 2) {
      return 'Please enter at least 2 characters'
    } else if (
      channels.selected.find(
        channel =>
          channel.toLocaleLowerCase() === trimChannel.toLocaleLowerCase(),
      )
    ) {
      return 'This channel has already been added to the active list'
    }
    return false
  }, [newChannel, channels.selected])

  return (
    <Card className={classes.spacing}>
      <CardContent>
        <Grid container spacing={4}>
          <Grid item xs={6} className={classes.spacing}>
            <h3 className={classes.h3}>Custom recruitment channels</h3>
            <Form onSubmit={createChannel}>
              <Grid container spacing={4}>
                <Grid item xs={7}>
                  <Input
                    placeholder="Channel"
                    value={newChannel}
                    useExternalValue={true}
                    onChange={({ target: { value } }) => setNewChannel(value)}
                  />
                  {error && <div className={classes.error}>{error}</div>}
                </Grid>
                <Grid item xs={5}>
                  <SubmitButton
                    value="Add channel"
                    disabled={error}
                    onClick={e => {
                      e.preventDefault()
                    }}
                  />
                </Grid>
              </Grid>
            </Form>
            <Grid container spacing={4} className={classes.spacing}>
              <Grid item xs={12}>
                <Grid container spacing={3}>
                  {channels.custom.map(item => (
                    <Grid item xs={12} lg={6} key={item} noWrap>
                      <Grid
                        container
                        alignItems="flex-start"
                        title={item}
                        spacing={1}
                      >
                        <Grid item xs={6} noWrap>
                          <Checkbox
                            shouldListenToInputValue={true}
                            input={{
                              value: channels.selected.includes(item),
                              title: `${
                                channels.selected.includes(item)
                                  ? 'Deactivate'
                                  : 'Activate'
                              } channel`,
                            }}
                            label={item}
                            onChange={e =>
                              selectChannel(item, e.target.checked)
                            }
                            className={classes.checkbox}
                          />
                        </Grid>
                        <Grid item xs={4}>
                          <Chip
                            label="Custom"
                            size="small"
                            className={classes.chip}
                          />
                        </Grid>
                        <Grid item xs={2}>
                          <button
                            type="button"
                            className={classes.deleteButton}
                            onClick={() => deleteChannel(item)}
                          >
                            <DeleteIcon
                              color="#FFFFFF"
                              fill="#707087"
                              fontSize="small"
                            />
                          </button>
                        </Grid>
                      </Grid>
                    </Grid>
                  ))}
                </Grid>
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={6} className={classes.spacing}>
            <h3 className={classes.h3}>Standard channels</h3>
            <Grid
              container
              spacing={2}
              className={classes.spacing}
              alignItems="flex-start"
            >
              <Grid item xs={12}>
                <Checkbox
                  shouldListenToInputValue={true}
                  input={{ value: channels.isAllActive }}
                  label={`Set all as active in ${country.name}`}
                  onChange={e => selectChannel('all', e.target.checked)}
                />
              </Grid>
              {channels.standard.map(item => (
                <Grid item xs={6} key={item}>
                  <Checkbox
                    shouldListenToInputValue={true}
                    input={{
                      value: channels.selected.includes(item),
                      title: `${
                        channels.selected.includes(item)
                          ? 'Deactivate'
                          : 'Activate'
                      } channel`,
                    }}
                    label={item}
                    onChange={e => selectChannel(item, e.target.checked)}
                  />
                </Grid>
              ))}
            </Grid>
          </Grid>
        </Grid>
        <Grid container spacing={2}>
          {minSelectedhannels && (
            <Grid item xs={12} className={cn(classes.error, 'text-center')}>
              Must select at least 3 channels
            </Grid>
          )}
          <Grid item xs={6}>
            <Grid container justifyContent="flex-end">
              <SubmitButton
                value="Cancel"
                onClick={() => setEdit(edit => ({ ...edit, mode: !edit.mode }))}
                variant="outlined"
              />
            </Grid>
          </Grid>
          <Grid item xs={6}>
            <SubmitButton
              value="Save changes"
              onClick={() => saveChannels()}
              disabled={minSelectedhannels}
            />
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  )
}

export default Edit
